import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useApi } from "../hooks/useApi";
import { useWallet } from "../hooks/useWallet";
import { WalletProviderChooser } from "../components/WalletProviderChooser";
import { EXPECTED_CHAIN_ID, EXPECTED_CHAIN_LABEL, MAX_MINT_QUANTITY, REFERRAL_REWARDS_ONCHAIN_ENABLED, envString } from "../utils/config";
import { readActiveReferralCode, referralPath } from "../utils/referral";

interface AuthNonceResponse {
  nonce: string;
  expiresAt: string;
}

interface AuthVerifyResponse {
  jwt: string;
  expiresAt: string;
}

const SITE_URL = envString("VITE_CODE_APE_SITE_URL") ?? "https://code-ape.com";
const CODEX_SKILL_URL = `${SITE_URL.replace(/\/+$/, "")}/skill/RUN_INVENTORY.md`;
const CODE_APE_API_URL = envString("VITE_API_PUBLIC_URL") ?? "https://api.code-ape.com";
const DEFAULT_AUTH_URI = `${window.location.origin}/auth`;
const AUTH_URI = envString("VITE_CODE_APE_SIWE_URI") ?? DEFAULT_AUTH_URI;
const PRESET_QUANTITIES = [1, 3, 5, 10].filter((count) => count <= MAX_MINT_QUANTITY);

export default function CodexAuthPage() {
  const api = useApi();
  const { account, connect, personalSign, isConnecting, error: walletError, walletOptions, selectedWalletId, selectWallet } = useWallet();
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [sessionPass, setSessionPass] = useState<string | null>(null);
  const [expiresAt, setExpiresAt] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [copyBlocked, setCopyBlocked] = useState(false);
  const [quantity, setQuantity] = useState(1);
  const [customQuantity, setCustomQuantity] = useState("");

  const domain = useMemo(() => window.location.host.toLowerCase(), []);
  const activeReferralCode = useMemo(() => readActiveReferralCode(window.location.search), []);
  const shortAccount = account ? `${account.slice(0, 6)}...${account.slice(-4)}` : "Not connected";
  const customQuantityValue = customQuantity.trim();
  const customQuantityNumber = Number(customQuantityValue);
  const customQuantityInvalid =
    customQuantityValue.length > 0 &&
    (!/^\d+$/.test(customQuantityValue) || !Number.isInteger(customQuantityNumber) || customQuantityNumber < 1 || customQuantityNumber > MAX_MINT_QUANTITY);
  const authDisabled = busy || isConnecting || customQuantityInvalid;
  const commandReferralCode = REFERRAL_REWARDS_ONCHAIN_ENABLED ? activeReferralCode : null;
  const codexCommand = sessionPass && !customQuantityInvalid ? buildCodexCommand(sessionPass, quantity, commandReferralCode) : "";

  async function authorizeCodex() {
    if (customQuantityInvalid) {
      setError(`Enter a quantity from 1 to ${MAX_MINT_QUANTITY} before authorizing the agent.`);
      return;
    }
    setBusy(true);
    setError(null);
    setCopied(false);
    setCopyBlocked(false);
    setStatus(account ? "Open your wallet and approve the CODE APE login message." : "Connecting wallet...");
    try {
      const connected = account ?? await connect({ forcePermission: true });
      if (!connected) {
        setStatus("Choose a wallet provider, open the wallet popup, and approve the connection request.");
        setBusy(false);
        return;
      }

      setStatus("Checking eligibility...");
      const nonceResponse = await api.post<AuthNonceResponse>("/auth/nonce", {
        address: connected,
        chainId: EXPECTED_CHAIN_ID
      });

      const message = buildSiweMessage({
        domain,
        address: connected,
        uri: AUTH_URI,
        nonce: nonceResponse.nonce
      });

      setStatus("Sign the login message. This sends no ETH.");
      const signature = await personalSign(message, connected);

      setStatus("Building the agent summon command...");
      const verifyResponse = await api.post<AuthVerifyResponse>("/auth/verify", { message, signature });
      const command = buildCodexCommand(verifyResponse.jwt, quantity, commandReferralCode);
      setSessionPass(verifyResponse.jwt);
      setExpiresAt(verifyResponse.expiresAt);

      try {
        await navigator.clipboard?.writeText(command);
        setCopied(true);
        setStatus("Copied. Return to Codex and send the summon command.");
      } catch {
        setCopyBlocked(true);
        setStatus("Command ready. Copy it below and send it to Codex.");
      }
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "Failed to authorize the agent.");
      setStatus(null);
    } finally {
      setBusy(false);
    }
  }

  async function copyCodexCommand() {
    if (!codexCommand) return;
    await navigator.clipboard?.writeText(codexCommand);
    setCopied(true);
    setCopyBlocked(false);
  }

  function selectPresetQuantity(count: number) {
    setQuantity(count);
    setCustomQuantity("");
    setError(null);
    setCopied(false);
  }

  function updateCustomQuantity(value: string) {
    setCustomQuantity(value);
    setError(null);
    setCopied(false);
    const nextQuantity = Number(value.trim());
    if (/^\d+$/.test(value.trim()) && Number.isInteger(nextQuantity) && nextQuantity >= 1 && nextQuantity <= MAX_MINT_QUANTITY) {
      setQuantity(nextQuantity);
    }
  }

  return (
    <main className="page">
      <div className="container" style={{ paddingBottom: "var(--space-24)" }}>
        <section className="card text-center" style={{ maxWidth: 980, margin: "0 auto", padding: "clamp(var(--space-6), 6vw, var(--space-12))" }}>
          <div className="badge badge-prime" style={{ marginBottom: "var(--space-4)" }}>AI AGENT AUTH</div>
          <h1 className="text-hero" style={{ marginBottom: "var(--space-4)" }}>
            Authorize your<br />AI agent.
          </h1>
          <p className="text-xl text-muted" style={{ lineHeight: 1.65, maxWidth: 760, margin: "0 auto var(--space-3)" }}>
            Recommended path: send the command to Codex. Compatible agents can use the same pass if they can run the public Skill and call the backend flow.
          </p>
          <p className="lede-alt" style={{ maxWidth: 760, margin: "0 auto var(--space-6)" }}>
            You are not authorizing a website to mint. You are creating one short-lived pass for the AI-agent handoff flow.
          </p>

          {walletError && <div className="alert alert-error" style={{ marginBottom: "var(--space-4)", textAlign: "left" }}>{walletError}</div>}
          {error && <div className="alert alert-error" style={{ marginBottom: "var(--space-4)", textAlign: "left" }}>{error}</div>}
          {status && <div className="alert alert-info" style={{ marginBottom: "var(--space-4)", textAlign: "left" }}>{status}</div>}
          {activeReferralCode && (
            <div className="alert alert-info" style={{ marginBottom: "var(--space-4)", textAlign: "left" }}>
              Invite code saved for this browser: <strong>{activeReferralCode}</strong>
            </div>
          )}

          <div className="summon-quantity" aria-label="Summon quantity">
            <div>
              <strong>How many Apes?</strong>
              <span>Default is 1. Max is {MAX_MINT_QUANTITY} per wallet during mint.</span>
            </div>
            <div className="summon-quantity__buttons">
              {PRESET_QUANTITIES.map((count) => (
                <button
                  key={count}
                  type="button"
                  className={!customQuantity && quantity === count ? "is-active" : ""}
                  onClick={() => selectPresetQuantity(count)}
                  disabled={busy || isConnecting}
                >
                  {count}
                </button>
              ))}
            </div>
            <label className="summon-quantity__custom">
              <span>Custom quantity</span>
              <input
                type="number"
                inputMode="numeric"
                min={1}
                max={MAX_MINT_QUANTITY}
                step={1}
                value={customQuantity}
                placeholder={`Custom, max ${MAX_MINT_QUANTITY}`}
                onChange={(event) => updateCustomQuantity(event.currentTarget.value)}
                disabled={busy || isConnecting}
                aria-invalid={customQuantityInvalid}
              />
            </label>
            {customQuantityInvalid && <span className="summon-quantity__error">Enter 1 to {MAX_MINT_QUANTITY}. Max is {MAX_MINT_QUANTITY} per wallet.</span>}
          </div>

          <WalletProviderChooser walletOptions={walletOptions} selectedWalletId={selectedWalletId} selectWallet={selectWallet} />

          <button className="btn btn-primary btn-lg" type="button" onClick={authorizeCodex} disabled={authDisabled} id="btn-authorize-codex" style={{ width: "min(100%, 520px)", justifyContent: "center" }}>
            {busy || isConnecting ? "Authorizing..." : sessionPass ? "Refresh Agent Command" : "Authorize AI Agent"}
          </button>

          <p className="text-sm text-muted" style={{ marginTop: "var(--space-4)", lineHeight: 1.7 }}>
            This is not a mint transaction. No ETH moves here. The mint link will ask for the final wallet transaction.
          </p>
          <p className="text-sm text-muted" style={{ marginTop: "var(--space-2)", lineHeight: 1.7 }}>
            Choose All wallets for mobile wallets or WalletConnect, or unlock your browser wallet before authorizing the agent.
          </p>

          {sessionPass && (
            <div style={{ marginTop: "var(--space-8)", textAlign: "left" }}>
              <div className="badge badge-gold" style={{ marginBottom: "var(--space-3)" }}>{copied ? "Ready for Codex" : "Agent command ready"}</div>
              <pre className="text-mono" style={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere", background: "rgba(0,255,65,0.07)", border: "1px solid var(--color-border)", borderRadius: "var(--radius)", padding: "var(--space-4)", color: "var(--text-primary)", lineHeight: 1.65, maxHeight: 260, overflow: "auto" }}>
                {codexCommand || `Enter a valid quantity from 1 to ${MAX_MINT_QUANTITY} to build the command.`}
              </pre>
              <div style={{ display: "flex", gap: "var(--space-3)", flexWrap: "wrap", marginTop: "var(--space-4)" }}>
                <button className="btn btn-primary" type="button" onClick={copyCodexCommand} disabled={!codexCommand}>Copy Command</button>
                <Link className="btn btn-secondary" to={referralPath("/mint", activeReferralCode)}>Open Mint Page</Link>
              </div>
              {copyBlocked && <p className="text-sm text-muted" style={{ marginTop: "var(--space-3)" }}>Your browser blocked auto-copy. Use Copy Command.</p>}
            </div>
          )}

          <details style={{ marginTop: "var(--space-8)", textAlign: "left" }}>
            <summary className="text-sm text-muted" style={{ cursor: "pointer" }}>What happens behind the button?</summary>
            <div className="dense-kv" style={{ marginTop: "var(--space-4)" }}>
              <InfoRow label="Wallet" value={shortAccount} />
              <InfoRow label="Gate" value={`Wallet balance check on ${EXPECTED_CHAIN_LABEL}`} />
              <InfoRow label="Quantity" value={`${quantity} Ape${quantity === 1 ? "" : "s"}`} />
              <InfoRow label="Skill" value={CODEX_SKILL_URL} />
              <InfoRow label="Pass" value={expiresAt ? `expires ${new Date(expiresAt).toLocaleString()}` : "not created"} />
            </div>
          </details>
        </section>
      </div>
    </main>
  );
}

function buildSiweMessage(input: { domain: string; address: `0x${string}`; uri: string; nonce: string }): string {
  return [
    `${input.domain} wants you to sign in with your Ethereum account:`,
    input.address,
    "",
    "Create a CODE APE AI-agent session pass.",
    "",
    `URI: ${input.uri}`,
    "Version: 1",
    `Chain ID: ${EXPECTED_CHAIN_ID}`,
    `Nonce: ${input.nonce}`,
    `Issued At: ${new Date().toISOString()}`
  ].join("\n");
}

function buildCodexCommand(sessionPass: string, quantity: number, referralCode: string | null): string {
  const summonPhrase = quantity <= 1 ? "prepare my CODE APE mint" : `prepare my ${quantity}-Ape CODE APE mint`;
  const agentRunId = `codeape-${Date.now()}`;
  const body = referralCode
    ? `{"quantity":${quantity},"agentRunId":"${agentRunId}","referralCode":"${referralCode}"}`
    : `{"quantity":${quantity},"agentRunId":"${agentRunId}"}`;
  return `Codex, read only ${CODEX_SKILL_URL} and ${summonPhrase} using this temporary CODE APE pass: ${sessionPass}. Do not generate images, inspect reference files, run optional health checks, or do web searches. Call POST ${CODE_APE_API_URL.replace(/\/+$/, "")}/handoff/from-inventory with Authorization Bearer pass and JSON ${body}. Return the mintUrl first.`;
}

function InfoRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="kv-row">
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}
